import { multi, named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	IAppWillRenderFirstPageHandler,
	IPageWillUnmountHandler,
	IStructureAPI,
	BIReporter,
	ILogger,
	BrowserWindow,
	NavigationParams,
	Experiments,
	IPageProvider,
	IPageInitializer,
	INavigationManager,
} from '@wix/thunderbolt-symbols'
import {
	CurrentRouteInfoSymbol,
	LifeCycle,
	SiteFeatureConfigSymbol,
	StructureAPI,
	BISymbol,
	LoggerSymbol,
	MasterPageFeatureConfigSymbol,
	InternalNavigationType,
	BrowserWindowSymbol,
	LOADING_PHASES,
	ExperimentsSymbol,
	PageTransitionsCompletedSymbol,
	PageProviderSymbol,
	PageInitializerSymbol,
	NavigationManagerSymbol,
} from '@wix/thunderbolt-symbols'
import { resolveUrl } from './resolveUrl'
import {
	name,
	RoutingMiddleware,
	UrlHistoryManagerSymbol,
	CustomUrlMiddlewareSymbol,
	PageJsonFileNameMiddlewareSymbol,
	CustomNotFoundPageMiddlewareSymbol,
	RouterContextSymbol,
	RoutingValidationSymbol,
	PageNumberSymbol,
	queryParamsWhitelistSymbol,
	RoutingBlockerManagerSymbol,
} from './symbols'
import type {
	ICurrentRouteInfo,
	ICustomUrlMiddleware,
	IRoutingMiddleware,
	IRouter,
	RouteInfo,
	IUrlHistoryManager,
	RouterMasterPageConfig,
	CandidateRouteInfo,
	IRouterContext,
	IRoutingValidation,
	IPageNumber,
	IRoutingBlockerManager,
	IQueryParamsWhitelistHandler,
} from './types'
import { errorPagesIds, isExperimentOpen, isSSR, taskify, yieldToMain } from '@wix/thunderbolt-commons'
import { reportNavigationEnd, reportNavigationStart } from './navigationMonitoring'
import { removeUrlHash, removeQueryParams, removeTrailingSlashAndQueryParams } from './urlUtils'
import type { IRoutingConfig } from '@wix/thunderbolt-types'

const emptyMiddleware: IRoutingMiddleware = {
	handle: async (routeInfo) => routeInfo,
}
const emptyCustomUrlMiddleware: ICustomUrlMiddleware = {
	handleSync: (routeInfo) => routeInfo,
	resolveCustomUrl: (routeInfo) => routeInfo,
}

const get404Page = (
	routingConfig: IRoutingConfig,
	experiments: Experiments
): { pageId: string; relativeUrl?: string } => {
	if (experiments['specs.thunderbolt.popupCustom404'] && routingConfig.customNotFoundPage?.pageId) {
		return {
			pageId: routingConfig.customNotFoundPage.pageId,
			relativeUrl: routingConfig.customNotFoundPage.pageRoute,
		}
	}
	return { pageId: errorPagesIds._404_dp }
}

const notifyPageWillUnmount = async (routeInfo: RouteInfo, pageProvider: IPageProvider) => {
	const { contextId, pageId } = routeInfo
	const pageReflector = await pageProvider(contextId, pageId)
	const handlers = await pageReflector.getAllImplementersOfAsync<IPageWillUnmountHandler>(
		LifeCycle.PageWillUnmountHandler
	)
	await Promise.all(handlers.map((handler) => handler.pageWillUnmount({ pageId, contextId })))
}

const hasPageTransitions = async (routeInfo: RouteInfo, pageProvider: IPageProvider) => {
	const { contextId, pageId } = routeInfo
	const pageReflector = await pageProvider(contextId, pageId)
	const [pageTransitionsImp] = pageReflector.getAllImplementersOf<any>(PageTransitionsCompletedSymbol)
	return !!pageTransitionsImp
}

const routerFactory = (
	routingConfig: IRoutingConfig,
	routingMasterPageConfig: RouterMasterPageConfig,
	structureApi: IStructureAPI,
	pageProvider: IPageProvider,
	routerContext: IRouterContext,
	routingValidation: IRoutingValidation,
	navigationManager: INavigationManager,
	pageJsonFileNameMiddleware: IRoutingMiddleware = emptyMiddleware,
	customNotFoundPageMiddleware: IRoutingMiddleware = emptyMiddleware,
	customUrlMiddleware: ICustomUrlMiddleware = emptyCustomUrlMiddleware,
	dynamicRoutingMiddleware: IRoutingMiddleware = emptyMiddleware,
	protectedRoutingMiddleware: IRoutingMiddleware = emptyMiddleware,
	BlockingDialogsRoutingMiddleware: IRoutingMiddleware = emptyMiddleware,
	pageNumberHandler: IPageNumber,
	{ initPage }: IPageInitializer,
	appWillRenderFirstPageHandlers: Array<IAppWillRenderFirstPageHandler>,
	currentRouteInfo: ICurrentRouteInfo,
	urlHistoryManager: IUrlHistoryManager,
	biReporter: BIReporter,
	logger: ILogger,
	window: BrowserWindow,
	queryParamHandler: IQueryParamsWhitelistHandler,
	routingBlockerManager: IRoutingBlockerManager,
	experiments: Experiments
): IRouter => {
	const convertToFullUrl = (url: string, removeSearchParamsAndHash: boolean) => {
		const isHomePageUrl = url === routingConfig.baseUrl || url === './'
		const candidateUrl = isHomePageUrl ? routingConfig.baseUrl : url
		const fullUrl = new URL(candidateUrl, `${routingConfig.baseUrl}/`)

		if (removeSearchParamsAndHash) {
			const urlWithoutQueryParam = removeQueryParams(fullUrl.href)
			return removeUrlHash(urlWithoutQueryParam)
		}
		return fullUrl.href
	}

	const buildRouteInfo = (url: string): Partial<CandidateRouteInfo> => {
		const fullURL = convertToFullUrl(url, false)
		const parserUrl = urlHistoryManager.getParsedUrl()
		const queryParams = queryParamHandler.getWhitelist(parserUrl)
		return resolveUrl(
			fullURL,
			routingConfig,
			{
				currentParsedUrl: parserUrl,
				queryParamsWhitelist: queryParams,
			},
			experiments,
			routingMasterPageConfig.tpaSectionPageIds
		)
	}

	const handleStaticRoute = async ({
		currentRoute,
		finalRouteInfo,
		navigationParams,
	}: {
		currentRoute: RouteInfo | null
		finalRouteInfo: CandidateRouteInfo
		navigationParams?: NavigationParams
	}) => {
		await yieldToMain()
		currentRouteInfo.updateCurrentRouteInfo(finalRouteInfo)
		const isSectionAnchorUrlHashFixOn = isExperimentOpen('specs.thunderbolt.fixSectionAnchorUrlHash', experiments)
		const { anchorDataId } = finalRouteInfo
		const urlForHistory =
			!isSectionAnchorUrlHashFixOn && anchorDataId && !finalRouteInfo.parsedUrl.hash && anchorDataId !== 'SCROLL_TO_TOP'
				? Object.assign(new URL(finalRouteInfo.parsedUrl), { hash: anchorDataId })
				: finalRouteInfo.parsedUrl

		urlHistoryManager.pushUrlState(urlForHistory, navigationParams)

		const { contextId, pageId } = finalRouteInfo
		await initPage({ pageId, contextId, anchorDataId: finalRouteInfo.anchorDataId })

		if (navigationManager.isFirstNavigation()) {
			const shouldReportAdditionalLoadingPhases = process.env.PACKAGE_NAME === 'thunderbolt-ds'
			if (shouldReportAdditionalLoadingPhases) {
				logger.phaseStarted(LOADING_PHASES.APP_WILL_RENDER_FIRST_PAGE)
			}
			await Promise.all(
				appWillRenderFirstPageHandlers.map((handler) =>
					taskify(() => handler.appWillRenderFirstPage({ pageId, contextId }))
				)
			)
			if (shouldReportAdditionalLoadingPhases) {
				logger.phaseEnded(LOADING_PHASES.APP_WILL_RENDER_FIRST_PAGE)
			}
		}

		navigationManager.setShouldBlockRender(false)
		structureApi.addPageAndRootToRenderedTree(
			pageId,
			contextId,
			routingMasterPageConfig.landingPagesIds[pageId],
			routingMasterPageConfig.isResponsive
		)
		const hasTransitions = await hasPageTransitions(finalRouteInfo, pageProvider)

		if (!navigationManager.isFirstPage() && !hasTransitions) {
			scrollToTop()
		}

		await yieldToMain()

		finalRouteInfo.biData = {
			...finalRouteInfo.biData,
			navAnimate: hasTransitions,
		}

		reportNavigationEnd(
			currentRoute,
			finalRouteInfo,
			InternalNavigationType.NAVIGATION,
			biReporter,
			logger,
			pageNumberHandler.getPageNumber()
		)

		return true
	}

	const scrollToTop = () => {
		if (!isSSR(window)) {
			window!.scrollTo({ top: 0 })
			if (experiments['specs.thunderbolt.postTransitionElementFocus']) {
				if (navigationManager.isFirstPage()) {
					const targetElement = window!.document.getElementById('SCROLL_TO_TOP')
					targetElement?.focus()
				} else {
					const firstSection = window.document.querySelector('main section') as HTMLElement
					firstSection?.focus({ preventScroll: true })
				}
			} else {
				const targetElement = window!.document.getElementById('SCROLL_TO_TOP')

				targetElement?.focus()
			}
		}
	}

	const handleSamePageNavigation = (
		currentRoute: RouteInfo | null,
		finalRouteInfo: CandidateRouteInfo,
		navigationParams?: NavigationParams
	) => {
		currentRouteInfo.updateCurrentRouteInfo(finalRouteInfo)
		urlHistoryManager.pushUrlState(finalRouteInfo.parsedUrl, navigationParams)

		const shouldScrollToTop = !navigationParams?.disableScrollToTop
		if (shouldScrollToTop) {
			scrollToTop()
		}
		finalRouteInfo.biData = {
			pageType: 'tpa-same-page',
		}
		reportNavigationEnd(
			currentRoute,
			finalRouteInfo,
			InternalNavigationType.INNER_ROUTE,
			biReporter,
			logger,
			pageNumberHandler.getPageNumber()
		)
	}

	let redirectCounter = 0

	const navigate = async (
		url: string,
		navigationParams?: NavigationParams,
		dynamicPageIdOverride?: string
	): Promise<boolean> => {
		const fullUrlRouteWithoutQueryParams = convertToFullUrl(url, true)
		const currentRoute = currentRouteInfo.getCurrentRouteInfo() as CandidateRouteInfo
		const currentRouteUrlWithoutQueryParams = currentRoute && convertToFullUrl(currentRoute.parsedUrl.href, true)
		const isSameDynamicPageOverrideAsCurrentPage =
			!experiments['specs.thunderbolt.routerDynamicPageOverride'] ||
			!dynamicPageIdOverride ||
			dynamicPageIdOverride === currentRoute?.pageId
		if (
			isSameDynamicPageOverrideAsCurrentPage &&
			routingValidation.isSamePageUrl(currentRouteUrlWithoutQueryParams, fullUrlRouteWithoutQueryParams)
		) {
			currentRouteInfo.updateCurrentRouteInfo({ ...currentRoute, anchorDataId: navigationParams?.anchorDataId })
			const fullUrlRouteWithQueryParams = new URL(convertToFullUrl(url, false))
			urlHistoryManager.pushUrlState(fullUrlRouteWithQueryParams)
			if (!fullUrlRouteWithQueryParams.hash && !navigationParams?.disableScrollToTop) {
				scrollToTop()
			}
			return false
		}

		let routeInfo: Partial<CandidateRouteInfo> | null = buildRouteInfo(url)

		routeInfo.biData = navigationParams?.biData

		pageNumberHandler.updatePageNumber()
		reportNavigationStart(currentRoute, routeInfo, biReporter, logger, pageNumberHandler.getPageNumber())
		currentRouteInfo.updateWantedRouteInfo(routeInfo)

		routeInfo = customUrlMiddleware.handleSync(routeInfo)

		navigationManager.startDataFetching()
		routeInfo = routeInfo && (await dynamicRoutingMiddleware.handle(routeInfo, dynamicPageIdOverride))
		navigationManager.endDataFetching()

		if (routeInfo && routeInfo.redirectUrl) {
			if (redirectCounter < 4) {
				redirectCounter++
				reportNavigationEnd(
					currentRoute,
					routeInfo,
					InternalNavigationType.DYNAMIC_REDIRECT,
					biReporter,
					logger,
					pageNumberHandler.getPageNumber()
				)
				return navigate(routeInfo.redirectUrl)
			}
			redirectCounter = 0
			reportNavigationEnd(
				currentRoute,
				routeInfo,
				InternalNavigationType.DYNAMIC_REDIRECT,
				biReporter,
				logger,
				pageNumberHandler.getPageNumber()
			)
			return false
		} else {
			redirectCounter = 0
		}

		routeInfo = routeInfo && (await customNotFoundPageMiddleware.handle(routeInfo))
		routeInfo = routeInfo && (await pageJsonFileNameMiddleware.handle(routeInfo))
		routeInfo = routeInfo && (await BlockingDialogsRoutingMiddleware.handle(routeInfo))
		routeInfo = routeInfo && (await protectedRoutingMiddleware.handle(routeInfo))

		if (!routeInfo) {
			reportNavigationEnd(
				currentRoute,
				routeInfo,
				InternalNavigationType.NAVIGATION_ERROR,
				biReporter,
				logger,
				pageNumberHandler.getPageNumber()
			)
			return false
		}

		if (!routeInfo.pageJsonFileName && protectedRoutingMiddleware !== emptyMiddleware) {
			reportNavigationEnd(
				currentRoute,
				routeInfo,
				InternalNavigationType.NAVIGATION_ERROR,
				biReporter,
				logger,
				pageNumberHandler.getPageNumber()
			)
			throw new Error(`did not find the json file name for pageId ${routeInfo.pageId}`)
		}

		if (navigationParams?.anchorDataId) {
			routeInfo.anchorDataId = navigationParams?.anchorDataId
		}

		if (routingMasterPageConfig.popupPages[routeInfo.pageId!]) {
			const { pageId, relativeUrl } = get404Page(routingConfig, experiments)
			routeInfo = { ...routeInfo, pageId, ...(relativeUrl && { relativeUrl }) }
		}

		routeInfo.contextId = routerContext.getRouteInfoContext(routeInfo as CandidateRouteInfo)

		const finalRouteInfo = routeInfo as CandidateRouteInfo

		if (routingValidation.isTpaSamePageNavigation(currentRoute, finalRouteInfo)) {
			handleSamePageNavigation(currentRoute, finalRouteInfo, navigationParams)
			return false
		}

		if (navigationManager.isDuringNavigation()) {
			const shouldContinue = await navigationManager.waitForShouldContinueNavigation()
			const currentRouteFullUrl = convertToFullUrl(
				(currentRouteInfo.getCurrentRouteInfo() as CandidateRouteInfo).parsedUrl.href,
				true
			)
			if (!shouldContinue || fullUrlRouteWithoutQueryParams === currentRouteFullUrl) {
				reportNavigationEnd(
					currentRoute,
					finalRouteInfo,
					InternalNavigationType.CANCELED,
					biReporter,
					logger,
					pageNumberHandler.getPageNumber()
				)
				return false
			}
		}

		navigationManager.startNavigation(finalRouteInfo.pageId, currentRoute?.pageId)
		navigationManager.setShouldBlockRender(true)

		if (currentRoute) {
			await routingBlockerManager.waitForAllListenersToResolve()
			await notifyPageWillUnmount(currentRoute, pageProvider)
		}

		return handleStaticRoute({ currentRoute, finalRouteInfo, navigationParams })
	}

	const isInternalValidRoute = (url: string): boolean => {
		const routeInfo = resolveUrl(url, routingConfig, {}, experiments)
		const customRoute = customUrlMiddleware.resolveCustomUrl(routeInfo)
		const { type, relativeUrl = '', pageId } = customRoute || routeInfo
		const isHomePage = routingConfig.mainPageId === pageId
		const relativeUrlToCheck = isHomePage ? relativeUrl : removeTrailingSlashAndQueryParams(relativeUrl)
		const relativeUrlDepth = relativeUrlToCheck.split('/').length
		const validStaticUrl = relativeUrlDepth === 2 || routingConfig.routes[relativeUrlToCheck]

		switch (type) {
			case 'Dynamic':
				return true
			case 'Static':
				// Static page with inner route is supported only in tpa pages and wixapps app-builder AppPages
				return !!(
					validStaticUrl ||
					routingMasterPageConfig.tpaSectionPageIds[pageId!] ||
					routingMasterPageConfig.appBuilderAppPageIds[pageId!]
				)
			default:
				logger.meter('navigation_error', {
					paramsOverrides: {
						evid: '26',
						errorInfo: `invalid url: ${url}`,
						errorType: 'invalid_routes',
						eventString: 'error',
					},
				})

				return false
		}
	}

	return {
		navigate,
		isInternalValidRoute,
		buildRouteInfo,
	}
}

export const Router = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		StructureAPI,
		PageProviderSymbol,
		RouterContextSymbol,
		RoutingValidationSymbol,
		NavigationManagerSymbol,
		optional(PageJsonFileNameMiddlewareSymbol),
		optional(CustomNotFoundPageMiddlewareSymbol),
		optional(CustomUrlMiddlewareSymbol),
		optional(RoutingMiddleware.Dynamic),
		optional(RoutingMiddleware.Protected),
		optional(RoutingMiddleware.BlockingDialogs),
		PageNumberSymbol,
		PageInitializerSymbol,
		multi(LifeCycle.AppWillRenderFirstPageHandler),
		CurrentRouteInfoSymbol,
		UrlHistoryManagerSymbol,
		BISymbol,
		LoggerSymbol,
		BrowserWindowSymbol,
		queryParamsWhitelistSymbol,
		RoutingBlockerManagerSymbol,
		ExperimentsSymbol,
	],
	routerFactory
)
